import { useState, useRef, useEffect, useCallback, KeyboardEvent } from 'react'
import { Pencil, Check, X, Loader2 } from 'lucide-react'

interface InlineEditFieldProps {
  label: string
  value: string | number | null
  fieldName: string
  type: 'text' | 'number' | 'select'
  options?: { value: string; label: string }[]
  validate?: (value: string) => string | null
  onSave: (fieldName: string, value: string | number) => Promise<void>
  disabled?: boolean
  placeholder?: string
}

export function InlineEditField({
  label,
  value,
  fieldName,
  type,
  options = [],
  validate,
  onSave,
  disabled = false,
  placeholder = '',
}: InlineEditFieldProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const displayValue = value !== null && value !== undefined ? String(value) : ''

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select()
      }
    }
  }, [isEditing])

  // Handle clicks outside the component
  useEffect(() => {
    if (!isEditing) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        // If there's an error, stay in edit mode
        if (error) return
        // If not loading, cancel editing
        if (!isLoading) {
          handleCancel()
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isEditing, error, isLoading])

  const enterEditMode = () => {
    if (disabled || isLoading) return
    setEditValue(displayValue)
    setError(null)
    setIsEditing(true)
  }

  const handleCancel = useCallback(() => {
    setIsEditing(false)
    setEditValue('')
    setError(null)
  }, [])

  const handleSave = useCallback(async () => {
    if (isLoading) return

    const valueToValidate = editValue

    // Run client-side validation
    if (validate) {
      const validationError = validate(valueToValidate)
      if (validationError) {
        setError(validationError)
        return
      }
    }

    // Determine the value to save
    const valueToSave: string | number =
      type === 'number' ? Number(valueToValidate) : valueToValidate

    setIsLoading(true)
    setError(null)

    try {
      await onSave(fieldName, valueToSave)
      setIsEditing(false)
      setEditValue('')
      setError(null)
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Save failed — please try again'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [editValue, fieldName, isLoading, onSave, type, validate])

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  const getDisplayLabel = (): string => {
    if (type === 'select' && options.length > 0) {
      const option = options.find((opt) => opt.value === displayValue)
      return option ? option.label : displayValue || placeholder || '—'
    }
    return displayValue || placeholder || '—'
  }

  // DISPLAY MODE
  if (!isEditing) {
    return (
      <div className="group" ref={containerRef}>
        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
          {label}
        </label>
        <div
          className={`flex items-center gap-2 py-1 px-2 rounded-md transition-colors ${
            disabled
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
          onClick={!disabled ? enterEditMode : undefined}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault()
              enterEditMode()
            }
          }}
          aria-label={`Edit ${label}`}
        >
          <span className="text-sm text-gray-900 dark:text-white flex-1">
            {getDisplayLabel()}
          </span>
          {!disabled && (
            <Pencil
              className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-hidden="true"
            />
          )}
        </div>
      </div>
    )
  }

  // EDIT MODE
  return (
    <div ref={containerRef}>
      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
        {label}
      </label>
      <div className="flex items-center gap-2">
        {type === 'select' ? (
          <select
            ref={inputRef as React.RefObject<HTMLSelectElement>}
            className={`flex-1 text-sm border rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white bg-white dark:bg-gray-700 ${
              error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value)
              if (error) setError(null)
            }}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            aria-invalid={!!error}
            aria-describedby={error ? `${fieldName}-error` : undefined}
          >
            <option value="" disabled>
              Select...
            </option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type={type}
            className={`flex-1 text-sm border rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white bg-white dark:bg-gray-700 placeholder-gray-400 dark:placeholder-gray-400 ${
              error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value)
              if (error) setError(null)
            }}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            placeholder={placeholder}
            aria-invalid={!!error}
            aria-describedby={error ? `${fieldName}-error` : undefined}
          />
        )}

        {isLoading ? (
          <Loader2
            className="w-5 h-5 text-primary-600 animate-spin flex-shrink-0"
            aria-label="Saving..."
          />
        ) : (
          <>
            <button
              type="button"
              onClick={handleSave}
              className="p-1 rounded hover:bg-green-50 text-green-600 transition-colors flex-shrink-0"
              aria-label="Save"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="p-1 rounded hover:bg-red-50 text-red-600 transition-colors flex-shrink-0"
              aria-label="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {error && (
        <p
          id={`${fieldName}-error`}
          className="mt-1 text-xs text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  )
}
